import { Routes, CanActivateFn, Router } from '@angular/router'
import { inject } from '@angular/core'
import { AuthGuard } from './guards/auth.guard'
import { PaymentPageComponent } from './pages/payment-page/payment-page.component'
import { PagoCompletadoComponent } from './pages/payment-page/payment-completado/pago-completado.component'
import { unsupportedRoleGuard } from './permissions/unsupported-role.guard'
import { patientsImportGuard, permissionGuard, informeConsultaGuard } from './permissions/permission.guard'
import { Permission } from './permissions/permission.types'
import { UserStore } from './store/user/user.store'
import { PermissionService } from './permissions/permission.service'

const loginRedirectGuard: CanActivateFn = () => {
  const userStore = inject(UserStore)
  const router = inject(Router)
  const permissionService = inject(PermissionService)

  if (userStore.isAuthenticated()) {
    return router.parseUrl(permissionService.getDefaultRoute())
  }

  return true
}

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [loginRedirectGuard],
    loadComponent: () => import('./pages/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'sin-permisos',
    canActivate: [AuthGuard],
    loadComponent: () => import('./pages/sin-permisos/sin-permisos.component').then((m) => m.SinPermisosComponent),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
  },
  {
    path: 'aviso_privacidad',
    loadComponent: () =>
      import('./pages/privacy-notice/privacy-notice.component').then((m) => m.PrivacyNoticeComponent),
  },
  {
    path: 'informativa_privacidad',
    loadComponent: () =>
      import('./pages/privacy-notice/privacy-notice.component').then((m) => m.PrivacyNoticeComponent),
  },
  {
    path: 'terminos-y-condiciones',
    loadComponent: () => import('./pages/terms-notice/terms-notice.component').then((m) => m.TermsNoticeComponent),
  },
  {
    path: 'datenschutzhinweis',
    loadComponent: () =>
      import('./pages/privacy-notice/privacy-notice.component').then((m) => m.PrivacyNoticeComponent),
  },
  { path: 'aviso-privacidad', redirectTo: 'aviso_privacidad', pathMatch: 'full' },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
  },
  {
    path: 'recuperar_clave/:token',
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
  },
  {
    path: 'completar_registro/:entityId',
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
  },
  {
    path: 'completar-registro',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/completar-registro/completar-registro.component').then((m) => m.CompletarRegistroComponent),
  },
  {
    path: 'aceptar-terminos',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/aceptar-terminos/aceptar-terminos.component').then((m) => m.AceptarTerminosComponent),
  },
  {
    path: 'pago/:consultationId',
    component: PaymentPageComponent,
  },
  {
    path: 'pago-completado',
    component: PagoCompletadoComponent,
  },
  {
    path: 'stripe/connect/callback',
    loadComponent: () =>
      import('./pages/stripe-connect-callback/stripe-connect-callback.component').then(
        (m) => m.StripeConnectCallbackComponent,
      ),
  },
  {
    path: 'autologin/whatsapp/:token',
    loadComponent: () =>
      import('./pages/autologin-whatsapp/autologin-whatsapp.component').then((m) => m.AutologinWhatsappComponent),
  },
  {
    path: 'consultas-delivery-codigos/:token',
    loadComponent: () =>
      import('./pages/consultas-delivery-codigos/consultas-delivery-codigos.component').then(
        (m) => m.ConsultasDeliveryCodigosComponent,
      ),
  },
  {
    path: 'valorar_consulta_one_click/:token',
    loadComponent: () =>
      import('./pages/consulta-rating-one-click/consulta-rating-one-click.component').then(
        (m) => m.ConsultaRatingOneClickComponent,
      ),
  },
  {
    path: '',
    canActivate: [AuthGuard, unsupportedRoleGuard],
    loadComponent: () => import('./pages/dashboard/dashboard.component').then((m) => m.DashboardComponent),
    children: [
      {
        path: 'home',
        canActivate: [permissionGuard(Permission.HomeView)],
        loadComponent: () => import('./pages/home/home.component').then((m) => m.HomeComponent),
      },
      {
        path: 'sala-espera',
        canActivate: [permissionGuard(Permission.AgendaOwn)],
        loadComponent: () => import('./pages/sala-espera/sala-espera.component').then((m) => m.SalaEsperaComponent),
      },
      { path: 'sala_espera', redirectTo: 'sala-espera', pathMatch: 'full' },
      {
        path: 'mi-perfil',
        canActivate: [permissionGuard(Permission.ProfileView)],
        loadComponent: () => import('./pages/mi-perfil/mi-perfil.component').then((m) => m.MiPerfilComponent),
      },
      { path: 'stripe/connect/callback', redirectTo: 'mi-perfil', pathMatch: 'full' },
      {
        path: 'ausencias',
        canActivate: [permissionGuard(Permission.AbsencesOwn, Permission.AbsencesTeam)],
        loadComponent: () => import('./pages/ausencias/ausencias.component').then((m) => m.AusenciasComponent),
      },
      {
        path: 'ficha-consulta/:id',
        canActivate: [permissionGuard(Permission.ConsultationAttend, Permission.TeamAgenda)],
        loadComponent: () =>
          import('./pages/ficha-consulta/ficha-consulta.component').then((m) => m.FichaConsultaComponent),
      },
      {
        path: 'informe-consulta/:id',
        canActivate: [informeConsultaGuard],
        loadComponent: () =>
          import('./pages/informe-consulta/informe-consulta.component').then((m) => m.InformeConsultaComponent),
      },
      {
        path: 'historial-consultas',
        canActivate: [permissionGuard(Permission.ConsultationsHistory)],
        loadComponent: () =>
          import('./pages/historial-consultas/historial-consultas.component').then(
            (m) => m.HistorialConsultasComponent,
          ),
      },
      {
        path: 'pacientes',
        canActivate: [permissionGuard(Permission.PatientsList)],
        loadComponent: () => import('./pages/pacientes/pacientes.component').then((m) => m.PacientesComponent),
      },
      {
        path: 'pacientes/:id/historia-clinica',
        canActivate: [permissionGuard(Permission.PatientsClinicalHistory)],
        loadComponent: () =>
          import('./pages/historia-clinica/historia-clinica.component').then((m) => m.HistoriaClinicaComponent),
      },
      {
        path: 'importacion-pacientes',
        canActivate: [patientsImportGuard],
        loadComponent: () =>
          import('./pages/importacion-pacientes/importacion-pacientes.component').then(
            (m) => m.ImportacionPacientesComponent,
          ),
      },
      {
        path: 'crear-consulta',
        canActivate: [permissionGuard(Permission.ConsultationsCreate)],
        loadComponent: () =>
          import('./pages/crear-consulta/crear-consulta.component').then((m) => m.CrearConsultaComponent),
      },
      {
        path: 'gestion-medicos',
        canActivate: [permissionGuard(Permission.TeamManageDoctors)],
        loadComponent: () =>
          import('./pages/gestion-medicos/gestion-medicos.component').then((m) => m.GestionMedicosComponent),
      },
      {
        path: 'gestion-medicos/:id/perfil',
        canActivate: [permissionGuard(Permission.TeamManageDoctors)],
        loadComponent: () =>
          import('./pages/medico-perfil/medico-perfil.component').then((m) => m.MedicoPerfilComponent),
      },
      {
        path: 'gestion-operadores',
        canActivate: [permissionGuard(Permission.TeamManageOperators)],
        loadComponent: () =>
          import('./pages/gestion-operadores/gestion-operadores.component').then((m) => m.GestionOperadoresComponent),
      },
      {
        path: 'gestion-operadores/:id/perfil',
        canActivate: [permissionGuard(Permission.TeamManageOperators)],
        loadComponent: () =>
          import('./pages/operador-perfil/operador-perfil.component').then((m) => m.OperadorPerfilComponent),
      },
      {
        path: 'agenda-equipo',
        canActivate: [permissionGuard(Permission.TeamAgenda)],
        loadComponent: () =>
          import('./pages/agenda-equipo/agenda-equipo.component').then((m) => m.AgendaEquipoComponent),
      },
      {
        path: 'calendario-consultas',
        canActivate: [permissionGuard(Permission.AgendaOwn)],
        loadComponent: () =>
          import('./pages/calendario-consultas/calendario-consultas.component').then(
            (m) => m.CalendarioConsultasComponent,
          ),
      },
      {
        path: 'preguntas_frecuentes',
        canActivate: [permissionGuard(Permission.Faq)],
        loadComponent: () => import('./pages/faq/faq.component').then((m) => m.FaqComponent),
      },
      {
        path: 'pagos-contratos-medicos',
        canActivate: [permissionGuard(Permission.DoctorContractPayments)],
        loadComponent: () =>
          import('./pages/pagos-contratos-medicos/pagos-contratos-medicos.component').then(
            (m) => m.PagosContratosMedicosComponent,
          ),
      },
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/default-route-redirect/default-route-redirect.component').then(
            (m) => m.DefaultRouteRedirectComponent,
          ),
      },
    ],
  },
  {
    path: 'one-click/:token',
    loadComponent: () => import('./pages/one-click/one-click.component').then((m) => m.OneClickComponent),
  },
  { path: '**', redirectTo: 'login' },
]
